/* RESPONSIBLE TEAM: team-reporting */

import { ChartDefinition, ChartSchema, Timezone } from '@/features/report-snapshots/api/schema';
import type { ChartType } from '@/features/report-snapshots/types';
import {
  extractNumericValue,
  extractRelativeValue,
  translateCellName,
} from '@/features/report-snapshots/utils/chart-data-helpers';
import { assignColorToSeriesItemViewBy } from '@/features/report-snapshots/components/charts/utils/chart-series-colors';
import { ChartSeries } from '@/features/report-snapshots/components/charts/utils/chart-data';
import { HighchartsSeriesType } from '@/features/report-snapshots/types';
import { fromFloatToPercentageString } from '@/features/report-snapshots/formatters/percentage';
import { I18n } from '@lingui/core';
import { formatTimestamp } from '@/features/report-snapshots/formatters/timestamp';

export function getStackedNonSegmentSeries(
  schema: ChartSchema,
  chartType: ChartType,
  chartDefinition: ChartDefinition,
  timezone: Timezone,
  i18n: I18n,
) {
  const absoluteValues = schema.values.map((value) => {
    const cellName = translateCellName(value['0'] as string, i18n);

    return {
      name: cellName,
      value: extractNumericValue(value['1']),
      originalValue: value['1'],
    };
  });
  const totalSumAbsoluteValues = absoluteValues.reduce((acc, curr) => acc + (curr.value ?? 0), 0);

  return absoluteValues.map((value) => {
    const absoluteValue = value.value;

    const relativeValue = extractRelativeValue(absoluteValue, totalSumAbsoluteValues);

    let formattedValue: string;
    if (typeof value.originalValue === 'object') {
      formattedValue = fromFloatToPercentageString(absoluteValue ?? 0);
    } else {
      formattedValue = String(absoluteValue);
    }

    if (chartDefinition.viewBy === 'time') {
      value.name = formatTimestamp(
        chartDefinition.viewByTimeInterval ?? 'week',
        value.name as unknown as number,
        timezone,
        i18n,
      );
    }
    const seriesItem: ChartSeries = {
      name: value.name,
      type: chartType as HighchartsSeriesType,
      data: [
        {
          name: value.name,
          y: relativeValue,
          formattedValue: formattedValue,
        },
      ],
    };

    assignColorToSeriesItemViewBy(seriesItem, chartDefinition);

    return seriesItem;
  });
}
